(() => {
  'use strict';
  if (window.OrienXHomeMotion) {
    window.OrienXHomeMotion.boot(document);
    return;
  }
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const pending = new Set();
  const running = new Map();
  const foodCards = new Set();
  const foodJobs = new WeakMap();
  const boundCards = new WeakSet();
  const supported = 'IntersectionObserver' in window;

  function finish(element) {
    const end = running.get(element);
    if (end) {
      element.removeEventListener('animationend', end);
      element.removeEventListener('animationcancel', end);
      running.delete(element);
    }
    pending.delete(element);
    element.classList.remove('is-home-pending', 'is-home-revealing');
    element.classList.add('is-home-revealed');
  }
  function reveal(element, immediate = false) {
    entryObserver?.unobserve(element);
    pending.delete(element);
    if (immediate || reduced.matches) { finish(element); return; }
    if (!element.classList.contains('is-home-pending')) return;
    const end = event => {
      if (event.target !== element || event.animationName !== 'ox-home-enter') return;
      finish(element);
    };
    running.set(element, end);
    element.addEventListener('animationend', end);
    element.addEventListener('animationcancel', end);
    // Explicit keyframes provide the starting pose without synchronous layout reads.
    element.classList.replace('is-home-pending', 'is-home-revealing');
  }
  const entryObserver = supported ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio >= .06) reveal(entry.target);
    });
  }, { threshold: .06, rootMargin: '0px 0px -96px 0px' }) : null;

  function register(bindings, element, type = 'up', delay = 0) {
    if (!element || element.dataset.oxHomeMotion || !entryObserver || reduced.matches) return;
    if (['SCRIPT', 'STYLE', 'TEMPLATE'].includes(element.tagName)) return;
    bindings.set(element, { type, delay: Math.min(delay, 240) });
  }
  function commitBindings(bindings) {
    const viewport = window.innerHeight;
    // Read every box before writing any classes/styles, avoiding startup layout thrash.
    const measured = [...bindings].map(([element, options]) => ({
      element, options, box: element.getBoundingClientRect()
    }));
    measured.forEach(({ element, options, box }) => {
      if (!box.width || !box.height) return;
      element.dataset.oxHomeMotion = options.type;
      element.style.setProperty('--ox-home-delay', `${options.delay}ms`);
      // Protect the initial viewport and restored scroll position, including the LCP.
      if (box.top < viewport * .94) {
        element.classList.add('is-home-revealed');
      } else {
        element.classList.add('is-home-pending');
        pending.add(element);
        entryObserver.observe(element);
      }
    });
  }
  function imageReady(image) {
    image.loading = 'eager';
    image.decoding = 'async';
    if (image.decode) return image.decode().then(() => image.naturalWidth > 0).catch(() => false);
    if (image.complete) return Promise.resolve(image.naturalWidth > 0);
    return new Promise(resolve => {
      image.addEventListener('load', () => resolve(true), { once: true });
      image.addEventListener('error', () => resolve(false), { once: true });
    });
  }
  function loadFood(card) {
    if (foodJobs.has(card)) return foodJobs.get(card);
    const template = card.querySelector('template[data-ox-home-food]');
    if (!template) return Promise.resolve();
    foodObserver?.unobserve(card);
    const fragment = template.content.cloneNode(true);
    const images = [...fragment.querySelectorAll('img')];
    template.replaceWith(fragment);
    // Inert template images can report complete with zero width. Start readiness
    // checks only after they belong to the active document.
    const ready = Promise.all(images.map(imageReady));
    const job = ready.then(results => {
      // Keep the original product visible if a deferred image fails.
      if (results.every(Boolean) && card.isConnected) card.classList.add('is-food-ready');
    }).catch(() => {});
    foodJobs.set(card, job);
    return job;
  }
  const foodObserver = supported ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && hover.matches) loadFood(entry.target);
    });
  }, { rootMargin: '200px', threshold: 0 }) : null;

  function prepareFood(scope) {
    scope.querySelectorAll('.rx-home .rx-card--food-hover, .ox-mg .rx-card--food-hover').forEach(card => {
      foodCards.add(card);
      if (!boundCards.has(card)) {
        boundCards.add(card);
        card.addEventListener('pointerenter', event => {
          if (hover.matches && event.pointerType !== 'touch') loadFood(card);
        }, { passive: true });
        card.addEventListener('focusin', event => {
          if (hover.matches || event.target.matches(':focus-visible')) loadFood(card);
        });
      }
      if (hover.matches) {
        if (foodObserver) foodObserver.observe(card);
        else loadFood(card);
      }
    });
  }
  function boot(scope) {
    if (!document.body.classList.contains('rx-home-page')) return;
    prepareFood(scope);
    const bindings = new Map();
    const bind = (element, type, delay) => register(bindings, element, type, delay);
    scope.querySelectorAll('.rx-home').forEach(home => {
      home.querySelectorAll('.rx-mud-categories > .rx-category').forEach(item => bind(item));
      home.querySelectorAll('.rx-section-head, .rx-container > .rx-kicker').forEach(item => bind(item, 'fade'));
      home.querySelectorAll('.rx-strip').forEach(strip => {
        strip.querySelectorAll('.rx-card').forEach((card, index) => bind(card, 'up', index * 110));
      });
      home.querySelectorAll('.rx-split__image').forEach(item => bind(item));
      home.querySelectorAll('.rx-split__copy').forEach(item => bind(item, 'up', 100));
      home.querySelectorAll('.rx-values').forEach(group => {
        [...group.children].forEach((item, index) => bind(item, 'up', index * 110));
      });
      home.querySelectorAll('.rx-article-strip').forEach(group => {
        [...group.children].forEach((item, index) => bind(item, 'fade', index * 110));
      });
      home.querySelectorAll('.rx-social > h2').forEach(item => bind(item, 'fade'));
      home.querySelectorAll('.rx-social__grid > a').forEach((item, index) => bind(item, 'up', index * 70));
    });
    scope.querySelectorAll('.ox-footer__main > *').forEach((item, index) => {
      bind(item, 'fade', (index % 4) * 60);
    });
    commitBindings(bindings);
  }
  function failOpen() {
    [...pending, ...running.keys()].forEach(finish);
    entryObserver?.disconnect();
  }
  function safelyBoot(scope) {
    try { boot(scope); } catch (_) { failOpen(); }
  }
  reduced.addEventListener('change', () => {
    if (reduced.matches) [...pending, ...running.keys()].forEach(element => reveal(element, true));
  });
  hover.addEventListener('change', () => {
    foodCards.forEach(card => {
      if (!card.isConnected) { foodObserver?.unobserve(card); foodCards.delete(card); }
      else if (hover.matches && foodObserver) foodObserver.observe(card);
      else foodObserver?.unobserve(card);
    });
  });
  document.addEventListener('focusin', event => {
    const target = event.target.closest('[data-ox-home-motion]');
    if (target) reveal(target, true);
  });
  document.addEventListener('shopify:section:load', event => safelyBoot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    pending.forEach(element => {
      if (event.target.contains(element)) {
        entryObserver?.unobserve(element);
        pending.delete(element);
      }
    });
    [...running.keys()].forEach(element => {
      if (event.target.contains(element)) finish(element);
    });
    foodCards.forEach(card => {
      if (event.target.contains(card)) { foodObserver?.unobserve(card); foodCards.delete(card); }
    });
  });
  window.OrienXHomeMotion = { boot: safelyBoot };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => safelyBoot(document), { once: true });
  } else {
    safelyBoot(document);
  }
})();

